import { useEffect, useRef } from "react";
import type { RefObject } from "react";

import { NAV, PHONE, WHATSAPP_URL } from "../content/site";
import { type Lang, LANG_LABEL, localePath, navHref } from "../i18n/lang";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  lang: Lang;
  pathname: string;
  returnFocusTo?: RefObject<HTMLButtonElement | null>;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function PhoneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.8 5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4 5.2 5.2 0 0 0 3.2.6 2.7 2.7 0 0 0 1.8-1.3c.2-.6.2-1.1.2-1.2-.1-.2-.3-.3-.5-.4z" />
    </svg>
  );
}

/** Full-height drawer for phones; every nav item and dropdown child, one column. */
export function MobileMenu({
  open,
  onClose,
  lang,
  pathname,
  returnFocusTo,
}: MobileMenuProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    openerRef.current = returnFocusTo?.current ?? document.activeElement;
    closeButtonRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") {
        return;
      }
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (openerRef.current instanceof HTMLElement) {
        openerRef.current.focus();
      }
    };
  }, [open, onClose, returnFocusTo]);

  if (!open) {
    return null;
  }
  const other: Lang = lang === "en" ? "hi" : "en";
  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex flex-col bg-ivory md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
    >
      <div className="flex items-center justify-between border-b border-hairline px-5 py-4">
        <span className="font-display text-lg text-ink">Menu</span>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="min-h-11 min-w-11 text-2xl text-ink"
          aria-label="Close menu"
        >
          ×
        </button>
      </div>
      <div className="flex items-center gap-4 border-b border-hairline px-5 py-2">
        <a
          href={PHONE.tel}
          className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink transition-colors hover:text-magenta"
        >
          <PhoneIcon />
          {PHONE.display}
        </a>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          aria-label="WhatsApp"
          className="flex min-h-11 min-w-11 items-center justify-center text-ink transition-colors hover:text-magenta"
        >
          <WhatsAppIcon />
        </a>
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-10 pt-2">
        <ul>
          {NAV.map((item) => (
            <li key={item.label} className="border-b border-hairline">
              <a
                href={navHref(item.href, lang)}
                className="block min-h-12 py-3 text-lg text-ink"
              >
                {item.label}
              </a>
              {item.children ? (
                <ul className="pb-2 pl-4">
                  {item.children.map((c) => (
                    <li key={c.label}>
                      <a
                        href={c.external ? c.href : localePath(c.href, lang)}
                        className="block min-h-11 py-2 text-base text-ink-soft"
                        target={c.external ? "_blank" : undefined}
                        rel={c.external ? "noreferrer" : undefined}
                      >
                        {c.label}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={localePath("/donate", lang)}
            className="inline-flex min-h-11 items-center rounded-full bg-crimson px-5 font-semibold text-white"
          >
            Donate
          </a>
          <a
            href={localePath("/register", lang)}
            className="inline-flex min-h-11 items-center rounded-full bg-magenta px-5 font-semibold text-white"
          >
            Register
          </a>
          <a
            href={localePath(pathname, other)}
            className="inline-flex min-h-11 items-center rounded-full border border-ink px-5 font-semibold text-ink"
            lang={other}
          >
            {LANG_LABEL[other]}
          </a>
        </div>
      </nav>
    </div>
  );
}
